import { PlanTier } from '@/actions/settings/billingActions'
import {
  SIGNUP_CADENCES,
  type CadencePrice,
  type CheckoutSelection,
  type SignupCadence,
  type SignupCard,
  type SignupPlan,
} from './signupPlans.types'

const TIER_RANK: Partial<Record<PlanTier, number>> = {
  [PlanTier.lite]: 0,
  [PlanTier.essential]: 1,
  [PlanTier.essentialPlus]: 2,
  [PlanTier.premium]: 3,
  [PlanTier.premiumPlus]: 4,
}

const PREMIUM_TIERS: readonly PlanTier[] = [
  PlanTier.essentialPlus,
  PlanTier.premiumPlus,
]

const isPlanTier = (value: string): value is PlanTier =>
  (Object.values(PlanTier) as string[]).includes(value)

/** Takes a bare `string` (not just `PlanTier`) so a `RankedTierOffer.tier` from the
 *  readiness page's API-mirrored type can reuse this without a cast. */
export const isPremiumTier = (tier: string | undefined): boolean =>
  tier !== undefined && (PREMIUM_TIERS as readonly string[]).includes(tier)

const withProduct = (
  plan: SignupPlan
): Partial<Record<SignupCadence, CadencePrice>> => {
  const prices: Partial<Record<SignupCadence, CadencePrice>> = {}
  if (plan.prices.month) {
    prices.month = { ...plan.prices.month, productId: plan.productId }
  }
  if (plan.prices.year) {
    prices.year = { ...plan.prices.year, productId: plan.productId }
  }
  return prices
}

export const mergePlansByTier = (plans: SignupPlan[]): SignupCard[] => {
  const byTier = new Map<PlanTier, SignupCard>()
  for (const plan of plans) {
    if (!isPlanTier(plan.tier)) continue
    const held = byTier.get(plan.tier)
    byTier.set(plan.tier, {
      tier: plan.tier,
      productId: held?.productId ?? plan.productId,
      displayName: held?.displayName ?? plan.displayName,
      prices: { ...withProduct(plan), ...held?.prices },
    })
  }
  return [...byTier.values()]
}

export const orderedCards = (
  plans: SignupPlan[],
  { excludeLite }: { excludeLite: boolean }
): SignupCard[] =>
  mergePlansByTier(plans)
    .filter((card) => TIER_RANK[card.tier] !== undefined)
    .filter((card) => !(excludeLite && card.tier === PlanTier.lite))
    .sort((a, b) => (TIER_RANK[a.tier] ?? 0) - (TIER_RANK[b.tier] ?? 0))

export const validateStoredTier = (
  stored: string | null | undefined,
  cards: SignupCard[]
): PlanTier | undefined => cards.find((card) => card.tier === stored)?.tier

const defaultTier = (cards: SignupCard[]): PlanTier | undefined =>
  cards.find((card) => card.tier !== PlanTier.lite)?.tier

export const selectPaymentTier = (
  stored: string | null,
  cards: SignupCard[],
  { fallBackWhenStale = false }: { fallBackWhenStale?: boolean } = {}
): PlanTier | undefined => {
  if (!stored) return defaultTier(cards)
  const offered = validateStoredTier(stored, cards)
  return offered ?? (fallBackWhenStale ? defaultTier(cards) : undefined)
}

export const selectionFor = (
  card: SignupCard | undefined,
  cadence: SignupCadence
): CheckoutSelection | undefined => {
  const price = card?.prices[cadence]
  // A null amount refuses the same way usePlanCheckout.ts refuses an unpriced tier:
  // nothing the customer can fix, so there is no selection to check out with.
  return price && typeof price.unitAmountInCents === 'number'
    ? { priceId: price.priceId, productId: price.productId }
    : undefined
}

/** A cadence the plan lists a price for, whether or not that price carries an amount. */
export const cadencesOf = (card: SignupCard | undefined): SignupCadence[] =>
  [SIGNUP_CADENCES.year, SIGNUP_CADENCES.month].filter(
    (cadence) => card?.prices[cadence]
  )

/** The chosen cadence while the plan prices it, else annual, else the one cadence it does price. */
export const effectiveCadence = (
  card: SignupCard | undefined,
  chosen: SignupCadence | null
): SignupCadence => {
  const available = cadencesOf(card)
  if (chosen && available.includes(chosen)) return chosen
  return available[0] ?? SIGNUP_CADENCES.year
}
